import { useForm } from 'react-hook-form';
import { Modal } from '../../../ui/components/Modal/Modal';
import { Heading } from '../../../ui/components/Heading/Heading';
import styles from './AppointmentModal.module.scss';
import { Input } from '../../../ui/components/Input/Input';
import {
  Dropdown,
  DropdownItemsProps,
} from '../../../ui/components/Dropdown/Dropdown';
import { Button } from '../../../ui/components/Button/Button';
import {
  SEND_MESSENGER_MESSAGE,
  SEND_WA_MESSAGE,
} from '../../../utils/socialLinks';
import { JESSICA_SERVICES } from './Services';
import { TextArea } from '../../../ui/components/TextArea/TextArea';
import { Icon } from '../../../ui/components/Icon';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultService?: DropdownItemsProps;
}

interface AppointmentForm {
  firstName: string;
  lastName: string;
  age: string;
  service: string;
  wayOfContact: 'WhatsApp' | 'Messenger';
  context?: string;
}

const ERROR_EMPTY = 'This information is required';

export const AppointmentModal = ({
  isOpen = false,
  onClose = () => {},
  defaultService,
}: AppointmentModalProps) => {
  const message = (name: string, service: string, context: string) =>
    `Hello Dr. Jessica.[break]I'm ${name} and I would like to book an appointment for ${service}.[break]Context: ${context}`;

  const {
    handleSubmit,
    register,
    setValue,
    setError,
    formState: { errors },
  } = useForm<AppointmentForm>();

  const onSubmit = (data: AppointmentForm) => {
    let READY = true;
    if (data.firstName === '') {
      setError('firstName', { message: ERROR_EMPTY });
      READY = false;
    }

    if (data.lastName === '') {
      setError('lastName', { message: ERROR_EMPTY });
      READY = false;
    }

    if (data.service === '') {
      setError('service', { message: ERROR_EMPTY });
      READY = false;
    }

    if (READY) {
      const MESSENGER_ID = 224230325045460;
      const TELEPHONE_NUMBER = 3310504287;
      const MESSAGE = message(
        `${data.firstName} ${data.lastName}`,
        data.service,
        data.context
      );

      if (data.wayOfContact === 'WhatsApp') {
        SEND_WA_MESSAGE(TELEPHONE_NUMBER.toString(), MESSAGE);
      }

      if (data.wayOfContact === 'Messenger') {
        SEND_MESSENGER_MESSAGE(MESSENGER_ID.toString(), MESSAGE);
      }
    }
  };

  return (
    <Modal
      className={styles.root}
      isOpen={isOpen}
      onClose={onClose}
      colorPalette="doctor-1"
    >
      <section className={styles.layout}>
        <aside className={styles.aside}>
          <span className={styles.eyebrow}>Quick request</span>
          <Heading className={styles.heading} headingLevel="h2">
            Book an appointment
          </Heading>
          <p>
            Share a few details and we will prepare a message with your
            preferred contact channel.
          </p>
          <div className={styles.steps} aria-label="Appointment request steps">
            <div className={styles.step}>
              <Icon type="medical" alt="" classname={styles.icon} />
              <span>Select the service</span>
            </div>
            <div className={styles.step}>
              <Icon type="whatsapp" alt="" classname={styles.icon} />
              <span>Choose where to send it</span>
            </div>
            <div className={styles.step}>
              <Icon type="calendar" alt="" classname={styles.icon} />
              <span>Confirm available times</span>
            </div>
          </div>
        </aside>
        <form
          className={styles.form}
          action=""
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={styles.grid}>
            <Input
              classname={styles.input}
              label="First Name"
              placeholder="Your Name"
              isRequired
              register={register('firstName')}
              errorMessage={errors?.firstName?.message}
            />
            <Input
              classname={styles.input}
              label="Last Name"
              placeholder="Your Last Name"
              isRequired
              register={register('lastName')}
              errorMessage={errors?.lastName?.message}
            />
            <Dropdown
              classname={styles.input}
              label="Service required"
              isRequired
              placeholder="Service to book"
              register={register('service')}
              name="service"
              errorMessage={errors?.service?.message}
              SetValue={setValue}
              items={JESSICA_SERVICES}
              defaultValue={defaultService}
            />
            <Dropdown
              classname={styles.input}
              label="Way Of Contact"
              isRequired
              placeholder="Way Of Contact"
              register={register('wayOfContact')}
              name="wayOfContact"
              errorMessage={errors?.wayOfContact?.message}
              SetValue={setValue}
              defaultValue={{
                placeholder: 'WhatsApp',
                value: 'WhatsApp',
              }}
              items={[
                {
                  placeholder: 'WhatsApp',
                  value: 'WhatsApp',
                },
                {
                  placeholder: 'Messenger',
                  value: 'Messenger',
                },
              ]}
            />
            <TextArea
              classname={styles.context}
              register={register('context')}
              name="context"
              label="Context (Optional)"
              errorMessage={errors?.context?.message}
              placeholder="Add symptoms, preferred days, or anything useful"
            />
          </div>
          <div className={styles.buttons}>
            <Button
              className={`${styles.button} ${styles['cancel-button']}`}
              text="Cancel"
              onClick={onClose}
              glassEffect
            />
            <Button
              className={`${styles.button} ${styles['submit-button']}`}
              text="Send request"
              btnStyle="secondary"
              type="submit"
              glassEffect
            />
          </div>
        </form>
      </section>
    </Modal>
  );
};
